/**
 * FDV — <fdv-product-description>
 *
 * Envuelve la descripción del producto en la PDP y, si `hide-redundant="true"`,
 * quita del DOM los trozos que ya se muestran en otros bloques de la columna:
 *   · el párrafo que empieza con "Aroma:"        → chips (fdv-aroma-notes)
 *   · el <li> "Envío gratis ... $699"            → fdv-reassurance + acordeón
 *   · el <li> "…elaboran artesanalmente… tiempos de entrega"  → idem
 *   · la cola de un <p> con "ENVÍO GRATIS" / "Tiempo de entrega:" suelta (velas
 *     con detalles en líneas <br>: vela-virgo, aquelarre, la-luna)
 *   · un <ul> que quedó vacío + su encabezado "Detalles mágicos:"
 *
 * Trabaja sobre el DOM renderizado (no sobre el string HTML) → tolera las
 * variaciones de markup entre productos. Sin JS, la descripción se ve completa
 * (progressive enhancement). NO toca datos de tienda: la descripción real en
 * Shopify no cambia, así que el tema en vivo (Impulse) no se ve afectado.
 */
class FdvProductDescription extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hideRedundant !== 'true') return;
    const root = this.querySelector('.rte') || this;
    try {
      this.#clean(root);
    } catch (e) {
      /* nunca romper la PDP por limpiar la descripción */
    }
  }

  #clean(root) {
    const topP = () => Array.from(root.children).filter((el) => el.tagName === 'P');

    // 1) <p> que empieza con "Aroma:"
    for (const p of topP()) {
      if (/^\s*aroma\s*:/i.test(p.textContent || '')) p.remove();
    }

    // 2) <li> de envío gratis / enlace de tiempos de entrega
    for (const li of root.querySelectorAll('li')) {
      const t = (li.textContent || '').trim();
      if (/env[íi]o\s+gratis/i.test(t) && /\b(699|599|1000|999|1299)\b/.test(t)) { li.remove(); continue; }
      if (/elaboran artesanalmente|conoce los tiempos de entrega/i.test(t)) li.remove();
    }

    // 3) cola de un <p> con "ENVÍO GRATIS" / "Tiempo de entrega:" en texto suelto
    for (const p of topP()) {
      if (!/env[íi]o\s+gratis|tiempo de entrega|recibe esta vela gratis/i.test(p.textContent || '')) continue;
      // <br> puede traer atributos (data-start/data-end de pegados) → [^>]*
      const parts = p.innerHTML.split(/<br[^>]*>/i);
      let cut = -1;
      for (let i = 0; i < parts.length; i++) {
        const txt = parts[i].replace(/<[^>]+>/g, '').replace(/&nbsp;| /g, ' ');
        if (/env[íi]o\s+gratis|tiempo de entrega|recibe esta vela gratis/i.test(txt)) { cut = i; break; }
      }
      if (cut === 0) { p.remove(); continue; }
      if (cut > 0) {
        p.innerHTML = parts
          .slice(0, cut)
          .join('<br>')
          .replace(/(?:\s|&nbsp;| |<br\s*\/?>|<em>\s*<\/em>|<i>\s*<\/i>)+$/i, '');
        if (!(p.textContent || '').trim()) p.remove();
      }
    }

    // 4) <ul> vacío + su encabezado "Detalles mágicos:" previo
    for (const ul of root.querySelectorAll('ul')) {
      if (ul.children.length > 0) continue;
      const prev = ul.previousElementSibling;
      if (prev && /^\s*detalles m[áa]gicos\s*:?\s*$/i.test(prev.textContent || '')) prev.remove();
      ul.remove();
    }

    // 5) párrafos que quedaron vacíos
    for (const p of topP()) {
      if (!(p.textContent || '').trim() && !p.querySelector('img,iframe')) p.remove();
    }
  }
}

if (!customElements.get('fdv-product-description')) {
  customElements.define('fdv-product-description', FdvProductDescription);
}
